import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import * as Sentry from '@sentry/react';

import { User, UserBalance } from '../common/interfaces/userBalance';
import { UserService } from '../services/user/UserService';
import { UserStatus } from '../common/enums/auth';
import { ROUTES } from '../constants/routes';
import { useLocation } from 'react-router-dom';

type UserContextType = {
  userBalance: number;
  userStatus: UserStatus;
  user: User | null;
  accessToDashboard: boolean;
  mobileUserAuthError: string | null;
};

type UserActionsContextType = {
  onChangeUserBalance: () => void;
  updateUserStatus: (newStatus: UserStatus) => void;
  fetchUser: () => Promise<void>;
  setAccessToDashboardCallback: (isOwnerAccessToDashboard: boolean) => void;
  onChangeMobileUserAuthError: (error: string) => void;
  setUserPhoneOrEmail: (data: { phone?: string; email?: string }) => void;
};
export type InterruptedTransactionType = {
  idTag: string;
  sessionId: number;
  connectorId: string;
};

export const UserContext = createContext<UserContextType | null>(null);
export const UserActionsContext = createContext<UserActionsContextType | null>(
  null,
);

const userService = new UserService();

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [userBalance, setUserBalance] = useState<number>(0);
  const [userStatus, setUserStatus] = useState<UserStatus>(UserStatus.INACTIVE);
  const [user, setUser] = useState<User | null>(null);
  const [mobileUserAuthError, setMobileUserAuthError] = useState<string | null>(
    null,
  );
  const [accessToDashboard, seAccessToDashboard] = useState<boolean>(false);

  const { pathname } = useLocation();

  const onChangeUserBalance = useCallback(async () => {
    getUSerBalance();
  }, []);

  const onChangeMobileUserAuthError = useCallback((error: string) => {
    setMobileUserAuthError(error);
  }, []);

  const getUSerBalance = async () => {
    try {
      const response: UserBalance = await userService.getUserBalanceAndStatus();

      setUserBalance(response.balance / 100);
      setUserStatus(response.status);
      // eslint-disable-next-line no-empty
    } catch {}
  };

  // Routes an anonymous visitor can legitimately be on: asking the API who
  // they are would only 401 and report a false error to Sentry. `/` counts —
  // it is a redirect stop on the way to the landing page or the cabinet, and
  // whichever route wins does its own fetching. `startsWith` covers the owner
  // sign-in screen and the legacy `/login-owner` that redirects onto it.
  const isAnonymousRoute =
    pathname === '/' ||
    pathname === `/${ROUTES.LANDING}` ||
    pathname.startsWith(`/${ROUTES.LOGIN}`) ||
    pathname.startsWith(`/${ROUTES.ORGANISATION_LOGIN}`) ||
    pathname.includes(`/${ROUTES.RESET_PASSWORD}`);

  const fetchUser = useCallback(async () => {
    if (!isAnonymousRoute) {
      try {
        const response = await userService.getUser();
        setUser(response);
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  }, [isAnonymousRoute]);

  const updateUserStatus = useCallback((newStatus: UserStatus) => {
    setUserStatus(newStatus);
  }, []);

  const setUserPhoneOrEmail = useCallback(
    (data: { phone?: string; email?: string }) => {
      setUser({
        ...data,
      });
    },
    [],
  );

  const setAccessToDashboardCallback = useCallback(
    (isAccessToDashboard: boolean) => {
      seAccessToDashboard(isAccessToDashboard);
    },
    [],
  );

  useEffect(() => {
    if (isAnonymousRoute) return;

    getUSerBalance();
    fetchUser();
  }, [fetchUser, isAnonymousRoute]);

  const value = useMemo(
    () => ({
      userBalance,
      userStatus,
      user,
      accessToDashboard,
      mobileUserAuthError,
    }),
    [userBalance, userStatus, user, accessToDashboard, mobileUserAuthError],
  );

  const actions = useMemo(
    () => ({
      onChangeUserBalance,
      updateUserStatus,
      fetchUser,
      setAccessToDashboardCallback,
      onChangeMobileUserAuthError,
      setUserPhoneOrEmail,
    }),
    [
      onChangeUserBalance,
      updateUserStatus,
      fetchUser,
      setAccessToDashboardCallback,
      onChangeMobileUserAuthError,
      setUserPhoneOrEmail,
    ],
  );

  return (
    <UserContext.Provider value={value}>
      <UserActionsContext.Provider value={actions}>
        {children}
      </UserActionsContext.Provider>
    </UserContext.Provider>
  );
}
